import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAppConfig } from '../../../hooks/useAppConfig';
import { useInventoryStats } from '../../../hooks/useInventoryStats';
import { UserRole, AppUser, VisibilityMatrixValue } from '../../../types';
import { normalizeOwnerName } from '../../../utils/ownerNormalization';
import { CheckIcon, XMarkIcon, InformationCircleIcon } from '../../../components/Icons';
import { AdminVisibilityMatrixHeader } from './visibility_matrix/AdminVisibilityMatrixHeader';
import { AdminVisibilityMatrixTable } from './visibility_matrix/AdminVisibilityMatrixTable';

interface AdminVisibilityMatrixSectionProps {
  appUser: AppUser | null;
}

export const AdminVisibilityMatrixSection: React.FC<AdminVisibilityMatrixSectionProps> = ({ appUser }) => {
  const { appConfig, updateAppConfigParameter, updateStatus } = useAppConfig();
  const { allProperties, fetchAllActiveProperties, isLoading: isLoadingProps } = useInventoryStats(appUser);

  const [localMatrix, setLocalMatrix] = useState<VisibilityMatrixValue>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchAllActiveProperties();
  }, [fetchAllActiveProperties]);

  useEffect(() => {
    if (appConfig?.visibility_matrix?.value) {
      setLocalMatrix(appConfig.visibility_matrix.value as VisibilityMatrixValue);
    }
  }, [appConfig]);

  const uniqueOwners = useMemo(() => {
    const rawOwners = allProperties.map((p) => p.owner).filter(Boolean);
    const normalizedSet = Array.from(new Set<string>(rawOwners.map((o) => normalizeOwnerName(o))));
    return normalizedSet.sort((a, b) => a.localeCompare(b));
  }, [allProperties]);

  const rolesToManage = useMemo(
    () => [
      UserRole.BROKER,
      UserRole.BROKER_PREMIUM,
      UserRole.BROKER_INTERNO,
      UserRole.COMERCIAL_INTERNO,
      UserRole.BROKERS_LEADS,
      UserRole.BROKER_EXTERIOR,
      UserRole.CLIENTE,
    ],
    []
  );

  const togglePermission = useCallback((role: string, rawOwner: string) => {
    const owner = normalizeOwnerName(rawOwner);
    setLocalMatrix((prev) => {
      const currentAllowed = prev[role] || [];
      if (currentAllowed.includes(owner)) {
        return { ...prev, [role]: currentAllowed.filter((o) => o !== owner) };
      } else {
        return { ...prev, [role]: [...currentAllowed, owner] };
      }
    });
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    await updateAppConfigParameter('visibility_matrix', {
      name: 'Matriz de Visibilidad por Fuente',
      value: localMatrix,
    });
    setIsSaving(false);
  };

  const isAllowed = useCallback(
    (role: string, rawOwner: string) => {
      const owner = normalizeOwnerName(rawOwner);
      return localMatrix[role]?.includes(owner) || false;
    },
    [localMatrix]
  );

  const status = updateStatus['visibility_matrix'];

  return (
    <div className="space-y-6 animate-fade-in">
      <AdminVisibilityMatrixHeader
        isLoadingProps={isLoadingProps}
        isSaving={isSaving}
        onRefresh={fetchAllActiveProperties}
        onSave={handleSave}
      />

      {status?.error && (
        <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-700 text-sm font-medium flex items-center gap-3">
          <XMarkIcon className="w-5 h-5" />
          {status.error}
        </div>
      )}

      {status?.success && (
        <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl text-emerald-700 text-sm font-medium flex items-center gap-3">
          <CheckIcon className="w-5 h-5" />
          Configuración guardada correctamente.
        </div>
      )}

      <AdminVisibilityMatrixTable
        rolesToManage={rolesToManage}
        uniqueOwners={uniqueOwners}
        isLoadingProps={isLoadingProps}
        isAllowed={isAllowed}
        onTogglePermission={togglePermission}
      />

      <div className="p-6 bg-amber-50 rounded-3xl border border-amber-100 flex items-start gap-4">
        <div className="p-2 bg-amber-100 rounded-xl">
          <InformationCircleIcon className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h4 className="text-sm font-black text-amber-900 uppercase tracking-tight">Nota Importante</h4>
          <p className="text-xs text-amber-700 mt-1 leading-relaxed">
            Los roles <strong>SUPERADMIN</strong>, <strong>ADMIN</strong> y <strong>OPERACION</strong> tienen acceso total
            a todas las fuentes por defecto y no se listan en esta matriz para evitar bloqueos accidentales.
          </p>
        </div>
      </div>
    </div>
  );
};
